<script setup>
  import { getEmployerAcronym, getEmployerLogo } from '~/utils/employer'

  definePageMeta({ layout: 'simple' })

  const loaded = ref(false)
  const loadError = ref(false)
  const { find } = useStrapi4()

  const employerList = [
    'ville-de-lille',
    'agglopaysbasque',
    'agence-nationale-de-l-habitat-anah',
    'sorbonne-universite',
    'departement-de-la-vendee',
    'atout-france',
    'ville-de-roubaix',
    'ville-de-soyaux',
    'ville-de-vitry-sur-seine',
    'office-francais-de-la-biodiversite',
    'autorite-nationale-des-jeux',
    'direction-des-achats-de-letat',
    'nantes-universite',
    'ville-de-nanterre',
    'institut-polytechnique-de-paris'
  ]

  const employers = reactive({
    ...(await useLazyAsyncData(
      'employers-logo',
      async () => {
        const res = await find('employers', {
          fields: ['name'],
          populate: {
            logo: { fields: ['url'] },
            careerSite: {
              fields: ['active', 'slug', 'acronym'],
              populate: {
                logo: { fields: ['url'] },
                cover: { fields: ['url'] }
              }
            }
          },
          filters: {
            slug: { $in: employerList }
          }
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })

  onMounted(() => {
    const script = document.createElement('script')
    script.async = true
    script.src =
      'https://api.plezi.co/api/v1/web_forms/scripts?tenant_id=65080723f420876f571f1279&form_id=651bda4ed59cbc49559b5b54&form_version=3&content_web_form_id=65490f1ed59cbc2cfd671939'
    script.onload = () => {
      loaded.value = true
      // Apply CSS after form is loaded
      const style = document.createElement('style')
      style.innerHTML = `
        #foss-65490f1ed59cbc2cfd671939 {
          font-family: 'Gravur Condensed' !important;
        }
        .form-control {
          box-shadow: none !important;
        }
        .control-label {
          font-size: 1rem !important;
        }
        .checkbox input[type="checkbox"] {
          size: 1rem !important;
          color: rgb(88 217 219) !important;
          margin: 0px 4px 3px 4px !important;
          margin-bottom: 3px !important;
          border-radius: 0.25rem !important;
          border: 1px solid rgb(88 217 219) !important;
          outline: none !important;
        }
        .plezi-submit-btn {
          font-family: 'Gilroy' !important;
          font-weight: 700 !important;
        }
        .plezi-submit-btn:hover {
          background-color: #1FB6BB !important;
        }
      `
      document.head.appendChild(style)
    }
    script.onerror = () => (loadError.value = true)
    document.getElementById('foss-65490f1ed59cbc2cfd671939').appendChild(script)
  })

  const scrollTop = () => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }

  useTitle('ATS')
</script>

<template>
  <section class="mx-auto flex max-w-screen-xl flex-col justify-evenly px-4 py-4 pt-8 text-lg md:flex-row md:pt-16">
    <div class="my-auto sm:m-6 md:w-1/2">
      <h1 class="font-special text-2xl font-bold max-md:text-center md:text-3xl lg:text-4xl">
        Le logiciel de recrutement<br />
        pensé pour le service public
      </h1>
      <p class="ml-4 py-4 text-xl font-bold md:ml-8 md:text-2xl">Simplifiez vos recrutements !</p>
      <ul class="ml-16 list-disc text-lg md:text-xl">
        <li>Page emploi clé en main</li>
        <li>Publication illimitée d'offres d'emploi</li>
        <li>Multidiffusion</li>
        <li>Espace collaboratif de gestion des candidatures</li>
        <li>CVthèque</li>
        <li>Statistiques</li>
      </ul>
    </div>
    <div class="mt-8 w-full text-center md:mt-0 md:w-1/2">
      <p class="font-special text-xl font-bold">Nos experts vous rappellent</p>
      <div class="mt-2 flex justify-center space-x-6">
        <NuxtImg class="size-16 rounded-full" alt="Sigrid" src="/images/sigrid-150x150.png" />
        <NuxtImg class="size-16 rounded-full" alt="Valentin" src="/images/valentin-150x150.webp" />
      </div>
      <form id="foss-65490f1ed59cbc2cfd671939"></form>
    </div>
  </section>

  <section class="mx-auto max-w-screen-xl px-4 py-4 pt-12 font-special font-bold md:pt-16">
    <h2 class="pb-6 text-center text-2xl md:text-3xl">Modernisez vos recrutements</h2>
    <div class="flex flex-wrap justify-evenly space-x-4">
      <div class="m-4 w-96 text-center">
        <NuxtImg class="mb-2 rounded-md shadow-lg" alt="Publication d'une offre" src="/images/ats1.webp" />
        <p>Publication et multidiffusion de vos offres</p>
      </div>
      <div class="m-4 w-96 text-center">
        <NuxtImg class="mb-2 rounded-md shadow-lg" alt="Gestion collaborative et simplifiée" src="/images/ats2.webp" />
        <p>Gestion collaborative et automatisée</p>
      </div>
      <div class="m-4 w-96 text-center">
        <NuxtImg class="mb-2 rounded-md shadow-lg" alt="Statistiques" src="/images/ats3.webp" />
        <p>CVthèque qualifiée</p>
      </div>
    </div>
  </section>

  <section class="mx-auto max-w-screen-xl px-4 py-4 pt-12 font-special font-bold md:pt-16">
    <div class="flex flex-col md:flex-row">
      <div class="flex w-full items-center justify-center md:w-1/2 md:justify-start">
        <h2 class="text-2xl max-md:text-center md:text-3xl">Vous avez 1 minute ? <br />Découvrez Profil Public en action !</h2>
      </div>
      <div class="w-full p-4 md:w-1/2">
        <iframe
          class="aspect-video w-full rounded-xl border-2"
          src="https://www.youtube-nocookie.com/embed/sWczv7LookE"
          title="YouTube video player Profil Public - Présentation du logiciel de recrutement"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </div>
    </div>
  </section>

  <section class="mx-auto max-w-screen-lg px-4 py-4 pt-12 font-special font-bold md:pt-16" v-if="employers.data && employers.data.length">
    <h2 class="mb-8 text-center text-2xl md:text-3xl">Ils recrutent avec Profil Public</h2>
    <div class="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-5">
      <div class="m-2 flex items-center space-x-2" v-for="employer in employers.data" :key="employer.id">
        <NuxtImg
          class="size-10 rounded-full shadow-lg"
          v-if="getEmployerLogo(employer)"
          :alt="employer.name"
          :src="useStrapiMedia(getEmployerLogo(employer).url)"
        />
        <p class="font-bold">{{ getEmployerAcronym(employer) && employer.name.length > 45 ? getEmployerAcronym(employer) : employer.name }}</p>
      </div>
    </div>
  </section>

  <section class="mx-auto max-w-screen-xl pt-4">
    <div class="m-4 flex flex-col rounded-xl bg-tarawera-900 px-4 py-4 text-center font-special font-bold text-white md:py-12">
      <h2 class="text-2xl md:text-3xl">Vous voulez en savoir + ?</h2>
      <div class="mt-4 scroll-smooth text-center">
        <Button @click="scrollTop()" size="lg">Réserver une démo</Button>
      </div>
    </div>
  </section>
</template>
